/**
 * Gamification admin config models — mirrors the operations backend contract at
 * `/operations/gamification/*` (branch `feature/gamification-engine`).
 *
 * Enums are string-literal unions (repo convention). `*Upsert` = request bodies,
 * plain `*Dto` = responses. Translation DTOs carry an optional `id` (response-only,
 * preserved on update for replace-all semantics).
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export type RoadmapScope = 'FESTIVAL' | 'PARTNER'
export type StepUnlockMode = 'DEFAULT' | 'DEPENDENT'
export type StepTargetType = 'QUEST' | 'QUEST_GROUP' | 'ROADMAP'
export type AvailabilityType = 'GENERAL' | 'DAILY' | 'WEEKLY'
export type QuestTypeKey =
  | 'TOPUP'
  | 'CONTINUOUS_TOPUP'
  | 'CONTINUOUS_SPENDING'
  | 'AVATAR_SELECT'
  | 'QR_SCANNING'
export type TopupMode = 'SINGLE' | 'CUMULATIVE' | 'ANY' | 'FIRST'
export type CardNetwork =
  | 'VISA'
  | 'MASTERCARD'
  | 'AMEX'
  | 'DINERS'
  | 'DISCOVER'
  | 'JCB'

export type RewardTypeKey =
  | 'COIN_VOUCHER'
  | 'DISCOUNT_VOUCHER'
  | 'TICKET'
  | 'HOTEL'
  | 'PHYSICAL'
export type TriggerType = 'CHALLENGE' | 'MILESTONE' | 'BADGE' | 'LEADERBOARD'
export type DepletionPolicy = 'BLOCK' | 'UNLIMITED'

export const REWARD_TYPE_KEYS: RewardTypeKey[] = [
  'COIN_VOUCHER',
  'DISCOUNT_VOUCHER',
  'TICKET',
  'HOTEL',
  'PHYSICAL',
]
export const TRIGGER_TYPES: TriggerType[] = [
  'CHALLENGE',
  'MILESTONE',
  'BADGE',
  'LEADERBOARD',
]
export const DEPLETION_POLICIES: DepletionPolicy[] = ['BLOCK', 'UNLIMITED']

export const ROADMAP_SCOPES: RoadmapScope[] = ['FESTIVAL', 'PARTNER']
export const STEP_UNLOCK_MODES: StepUnlockMode[] = ['DEFAULT', 'DEPENDENT']
export const STEP_TARGET_TYPES: StepTargetType[] = [
  'QUEST',
  'QUEST_GROUP',
  'ROADMAP',
]
export const AVAILABILITY_TYPES: AvailabilityType[] = [
  'GENERAL',
  'DAILY',
  'WEEKLY',
]
export const QUEST_TYPE_KEYS: QuestTypeKey[] = [
  'TOPUP',
  'CONTINUOUS_TOPUP',
  'CONTINUOUS_SPENDING',
  'AVATAR_SELECT',
  'QR_SCANNING',
]

/** Never-completing earner types (no fixed points, always GENERAL, one active per roadmap each). */
export const CONTINUOUS_QUEST_TYPES: QuestTypeKey[] = [
  'CONTINUOUS_TOPUP',
  'CONTINUOUS_SPENDING',
]
export const TOPUP_MODES: TopupMode[] = ['SINGLE', 'CUMULATIVE', 'ANY', 'FIRST']
export const CARD_NETWORKS: CardNetwork[] = [
  'VISA',
  'MASTERCARD',
  'AMEX',
  'DINERS',
  'DISCOVER',
  'JCB',
]

/** TOPUP modes that require a positive `minAmount`. */
export const TOPUP_MODES_REQUIRING_MIN_AMOUNT: TopupMode[] = [
  'SINGLE',
  'CUMULATIVE',
]

// ---------------------------------------------------------------------------
// Translations (id? = response-only, preserved on update)
// ---------------------------------------------------------------------------

export interface RoadmapTranslationDto {
  id?: string
  locale: string
  title?: string
  subtitle?: string
}

export interface QuestGroupTranslationDto {
  id?: string
  locale: string
  title?: string
  subtitle?: string
  /** HTML authored here and rendered by the client apps. */
  instructions?: string
}

export interface QuestTranslationDto {
  id?: string
  locale: string
  title?: string
  description?: string
  instructions?: string
}

export interface BadgeTranslationDto {
  id?: string
  locale: string
  name?: string
}

export interface TagTranslationDto {
  id?: string
  locale: string
  text?: string
}

// ---------------------------------------------------------------------------
// Tag (edition-scoped, reusable label attachable to quests + quest groups)
// ---------------------------------------------------------------------------

/**
 * Colours are `#AARRGGBB` (alpha first) — NOT CSS hex, same convention as
 * `RoadmapThemeDto`. Convert with `argbToCssHex`/`cssHexToArgb`
 * (`@/lib/color-format`) before handing values to the picker/CSS.
 */
export interface TagDto {
  id: string
  festivalEditionId: string
  textColor: string
  backgroundColor: string
  translations: TagTranslationDto[]
}

export interface TagUpsertDto {
  textColor: string
  backgroundColor: string
  translations: TagTranslationDto[]
}

// ---------------------------------------------------------------------------
// Partner
// ---------------------------------------------------------------------------

export interface PartnerDto {
  id: string
  festivalEditionId: string
  name: string
  active: boolean
  ordinal: number
}

export interface PartnerUpsertDto {
  name: string
  active?: boolean
  ordinal?: number
}

// ---------------------------------------------------------------------------
// Available Token Pool
// ---------------------------------------------------------------------------

export interface AvailableTokenPool {
  id: string
  festivalEditionId: string
  name: string
  active: boolean
}

// ---------------------------------------------------------------------------
// Quest config (Jackson-polymorphic on `typeKey`)
// ---------------------------------------------------------------------------

export interface TopupQuestConfigDto {
  typeKey: 'TOPUP'
  mode: TopupMode
  /** BigDecimal on the wire; required & > 0 when mode is SINGLE/CUMULATIVE. */
  minAmount?: number
  /** null/undefined = all networks. */
  cardNetworks?: CardNetwork[]
}

/**
 * Continuous top-up earner: never completes. Credits `floor(depositedAmount × pointsPerUnit)` points
 * (default festival currency) on every qualifying top-up. At most one active per roadmap.
 */
export interface ContinuousTopupQuestConfigDto {
  typeKey: 'CONTINUOUS_TOPUP'
  /** BigDecimal on the wire; required & > 0. Points per 1 unit of default currency. May be fractional. */
  pointsPerUnit?: number
  /** null/undefined = all networks. */
  cardNetworks?: CardNetwork[]
  /** Optional token pool; when set, this quest routes through the pool instead of crediting/earning on the raw amount. */
  availableTokenPoolId?: string
}

/**
 * Continuous spending earner: never completes. Credits `floor(spentAmount × pointsPerUnit)` points
 * (default festival currency) on every wristband spend of real (non-reward) funds. No card filter.
 * At most one active per roadmap.
 */
export interface ContinuousSpendingQuestConfigDto {
  typeKey: 'CONTINUOUS_SPENDING'
  /** BigDecimal on the wire; required & > 0. Points per 1 unit of default currency spent. May be fractional. */
  pointsPerUnit?: number
  /** Optional token pool; when set, this quest routes through the pool instead of crediting/earning on the raw amount. */
  availableTokenPoolId?: string
}

/** One selectable option of an AVATAR_SELECT quest. `id` is server-assigned (present on read). */
export interface AvatarOptionDto {
  id?: string
  /** Uploaded image URL for the option's photo. */
  photoRef: string
  /** locale -> label. */
  labels: Record<string, string>
}

/**
 * Interactive single-choice quest: the user picks one option (photo + localized label); the choice is
 * recorded and the quest's fixed `points` are awarded once. Always GENERAL availability.
 */
export interface AvatarSelectQuestConfigDto {
  typeKey: 'AVATAR_SELECT'
  options: AvatarOptionDto[]
}

/**
 * QR-scanning quest: the user scans a QR encoding the deep link
 * `/roadmap?qr_scan={questId}&code={scanCode}` and confirms the scan to earn the quest's fixed
 * points once. The location + radius are used by the client to geofence before confirming.
 */
export interface QrScanningQuestConfigDto {
  typeKey: 'QR_SCANNING'
  /** Decimal degrees [-90, 90]. */
  latitude?: number
  /** Decimal degrees [-180, 180]. */
  longitude?: number
  /** Metres; must be > 0. */
  validationRadius?: number
  /**
   * Server-generated random 12-char alphanumeric secret embedded in the deep link's `code=` param.
   * Read-only — minted by the backend once the quest is first saved; never sent by the client.
   */
  scanCode?: string | null
}

/** Union grows as more quest types are added server-side. */
export type QuestConfigDto =
  | TopupQuestConfigDto
  | ContinuousTopupQuestConfigDto
  | ContinuousSpendingQuestConfigDto
  | AvatarSelectQuestConfigDto
  | QrScanningQuestConfigDto

/** Fresh default config for a quest type — used when creating or switching type. */
export const emptyQuestConfig = (typeKey: QuestTypeKey): QuestConfigDto => {
  switch (typeKey) {
    case 'TOPUP':
      return { typeKey: 'TOPUP', mode: 'ANY' }
    case 'CONTINUOUS_TOPUP':
      return { typeKey: 'CONTINUOUS_TOPUP' }
    case 'CONTINUOUS_SPENDING':
      return { typeKey: 'CONTINUOUS_SPENDING' }
    case 'AVATAR_SELECT':
      return { typeKey: 'AVATAR_SELECT', options: [] }
    case 'QR_SCANNING':
      return { typeKey: 'QR_SCANNING' }
  }
}

// ---------------------------------------------------------------------------
// Quest
// ---------------------------------------------------------------------------

export interface QuestDto {
  id: string
  questGroupId: string
  typeKey: QuestTypeKey
  availabilityType: AvailabilityType
  points: number
  timeWindowStart?: string | null
  timeWindowEnd?: string | null
  ordinal: number
  active: boolean
  config: QuestConfigDto
  configVersion: number
  translations: QuestTranslationDto[]
  /**
   * Deep link to encode into the QR: `/roadmap?qr_scan={id}&code={scanCode}`. Server-generated
   * (the `code` is minted once the quest is first saved) — use verbatim, never reconstruct
   * client-side. Only present for QR_SCANNING quests, and only once the quest has been saved.
   */
  deepLink?: string | null
  /**
   * Ordered tags attached to this quest. Not part of the main quest wire response — hydrated
   * separately via `GET /quests/{id}/tags` and carried here for the editor's convenience.
   */
  tags?: TagDto[]
}

export interface QuestUpsertDto {
  availabilityType: AvailabilityType
  points: number
  timeWindowStart?: string | null
  timeWindowEnd?: string | null
  ordinal?: number
  active?: boolean
  /** `typeKey` is derived server-side from config; do not send it separately. */
  config: QuestConfigDto
  translations: QuestTranslationDto[]
}

// ---------------------------------------------------------------------------
// Quest Group
// ---------------------------------------------------------------------------

export interface QuestGroupDto {
  id: string
  roadmapId: string
  availabilityType: AvailabilityType
  completeCount: number
  active: boolean
  ordinal: number
  translations: QuestGroupTranslationDto[]
  quests: QuestDto[]
  /**
   * Ordered tags attached to this quest group itself (distinct from its member quests' own
   * tags). Not part of the main quest-group wire response — hydrated separately via
   * `GET /quest-groups/{id}/tags` and carried here for the editor's convenience.
   */
  tags?: TagDto[]
}

export interface QuestGroupUpsertDto {
  availabilityType: AvailabilityType
  completeCount: number
  active?: boolean
  ordinal?: number
  translations: QuestGroupTranslationDto[]
}

// ---------------------------------------------------------------------------
// Roadmap
// ---------------------------------------------------------------------------

/**
 * Colour theme for a roadmap: one main colour plus two gradients (GRADIENT_1,
 * GRADIENT_2), each with its own fallback. Flat, mirroring the operations admin
 * contract. Colours are `#AARRGGBB` (alpha first) — NOT CSS hex. Convert with
 * `argbToCssHex`/`cssHexToArgb` (`@/lib/color-format`) before handing values to
 * the picker/CSS.
 */
export interface RoadmapThemeDto {
  mainColor: string
  gradient1FromColor: string
  gradient1ToColor: string
  gradient1FallbackColor: string
  gradient2FromColor: string
  gradient2ToColor: string
  gradient2FallbackColor: string
}

export interface RoadmapDto {
  id: string
  festivalEditionId: string
  scope: RoadmapScope
  partnerId?: string | null
  /** Optional logo (uploaded image URL); present on the full DTO and the nested step-target ref. */
  logoRef?: string | null
  active: boolean
  ordinal: number
  translations: RoadmapTranslationDto[]
  /** null on the shallow list projection and when nested as a step target. */
  steps: StepDto[] | null
  /** Optional colour theme; null/absent when not configured. */
  theme?: RoadmapThemeDto | null
}

export interface RoadmapUpsertDto {
  scope: RoadmapScope
  /** Required when scope = PARTNER. */
  partnerId?: string | null
  /** Optional logo (uploaded image URL) shown for the roadmap. */
  logoRef?: string | null
  active?: boolean
  ordinal?: number
  translations: RoadmapTranslationDto[]
  /** Optional colour theme. Sending `null` explicitly CLEARS the theme. */
  theme?: RoadmapThemeDto | null
}

// ---------------------------------------------------------------------------
// Step (+ polymorphic target discriminated by `targetType`)
// ---------------------------------------------------------------------------

export interface StepDto {
  id: string
  roadmapId: string
  ordinal: number
  unlockMode: StepUnlockMode
  targetType?: StepTargetType | null
  targetId?: string | null
  /**
   * Resolved server-side from targetType/targetId. Flat object of the concrete
   * type (no discriminator inside) — use `narrowStepTarget` to read it safely.
   */
  target?: QuestDto | QuestGroupDto | RoadmapDto | null
  badgeId?: string | null
  /** Optional 42x42 icon (uploaded image URL) shown for this step. */
  iconRef?: string | null
  active: boolean
  /** Active but excluded from the consumer roadmap UI (e.g. a pool-feeding topup step). */
  hidden: boolean
  /** Ids of steps that must be COMPLETED before this step unlocks. */
  dependencies: string[]
}

export interface StepUpsertDto {
  ordinal?: number
  unlockMode: StepUnlockMode
  targetType?: StepTargetType | null
  /** Required when targetType is set. */
  targetId?: string | null
  badgeId?: string | null
  /** Optional 42x42 icon (uploaded image URL) shown for this step. */
  iconRef?: string | null
  active?: boolean
  /** Active but excluded from the consumer roadmap UI. Null/undefined preserves current. */
  hidden?: boolean
}

export interface StepDependencyDto {
  dependsOnStepId: string
}

export interface StepDependencyResponseDto {
  id: string
  stepId: string
  dependsOnStepId: string
}

/** Discriminated view of a step's target, narrowed from the sibling `targetType`. */
export type NarrowedStepTarget =
  | { targetType: 'QUEST'; target: QuestDto | null }
  | { targetType: 'QUEST_GROUP'; target: QuestGroupDto | null }
  | { targetType: 'ROADMAP'; target: RoadmapDto | null }
  | { targetType: null; target: null }

export const narrowStepTarget = (step: StepDto): NarrowedStepTarget => {
  switch (step.targetType) {
    case 'QUEST':
      return { targetType: 'QUEST', target: (step.target as QuestDto) ?? null }
    case 'QUEST_GROUP':
      return {
        targetType: 'QUEST_GROUP',
        target: (step.target as QuestGroupDto) ?? null,
      }
    case 'ROADMAP':
      return {
        targetType: 'ROADMAP',
        target: (step.target as RoadmapDto) ?? null,
      }
    default:
      return { targetType: null, target: null }
  }
}

// ---------------------------------------------------------------------------
// Step-target authoring drafts (client-side)
//
// The step editor authors its target inline: a single (standalone) quest, a quest
// group + its quests, or a reference to an existing roadmap. These draft types hold
// the in-progress values (with optional `id`s once persisted) before `saveStep`
// orchestrates the create/update calls. See builder/saveStep.ts.
// ---------------------------------------------------------------------------

/** A quest being authored inline. `id` present ⇒ it already exists (update, not create). */
export interface QuestDraft extends QuestUpsertDto {
  id?: string
  /**
   * Server-provided QR_SCANNING deep link (see `QuestDto.deepLink`), carried along for display
   * only. Never round-tripped back to the server — `questDraftToUpsert` deliberately omits it.
   */
  deepLink?: string | null
  /** Ordered tags currently selected for this quest. Reconciled against the server by `saveStep`. */
  tags?: TagDto[]
}

/** A quest group being authored inline as a step's target. */
export interface QuestGroupDraft {
  /** Present ⇒ the group already exists (update, not create). */
  id?: string
  meta: QuestGroupUpsertDto
  quests: QuestDraft[]
  /** Ids of previously-persisted quests removed during this edit (deleted on save). */
  removedQuestIds: string[]
  /** Ordered tags currently selected for the group itself. Reconciled against the server by `saveStep`. */
  tags?: TagDto[]
}

/** Polymorphic target authored in the step editor, discriminated by `kind`. */
export type TargetDraft =
  | { kind: 'NONE' }
  | { kind: 'QUEST'; quest: QuestDraft | null }
  | { kind: 'QUEST_GROUP'; group: QuestGroupDraft }
  | { kind: 'ROADMAP'; roadmapId: string | null }

export type TargetDraftKind = TargetDraft['kind']

export const questDtoToDraft = (q: QuestDto): QuestDraft => ({
  id: q.id,
  availabilityType: q.availabilityType,
  points: q.points,
  timeWindowStart: q.timeWindowStart,
  timeWindowEnd: q.timeWindowEnd,
  ordinal: q.ordinal,
  active: q.active,
  config: q.config,
  translations: q.translations ?? [],
  deepLink: q.deepLink,
  tags: q.tags ?? [],
})

/** Strips the client-only `id` so the result is a clean upsert body. */
export const questDraftToUpsert = (q: QuestDraft): QuestUpsertDto => ({
  availabilityType: q.availabilityType,
  points: q.points,
  timeWindowStart: q.timeWindowStart,
  timeWindowEnd: q.timeWindowEnd,
  ordinal: q.ordinal,
  active: q.active,
  config: q.config,
  translations: q.translations,
})

export const emptyGroupDraft = (): QuestGroupDraft => ({
  meta: {
    availabilityType: 'GENERAL',
    completeCount: 1,
    active: true,
    translations: [],
  },
  quests: [],
  removedQuestIds: [],
  tags: [],
})

export const emptyTargetDraft = (kind: TargetDraftKind): TargetDraft => {
  switch (kind) {
    case 'QUEST':
      return { kind: 'QUEST', quest: null }
    case 'QUEST_GROUP':
      return { kind: 'QUEST_GROUP', group: emptyGroupDraft() }
    case 'ROADMAP':
      return { kind: 'ROADMAP', roadmapId: null }
    default:
      return { kind: 'NONE' }
  }
}

/** Hydrates a target draft from an existing step's resolved target (for edit mode). */
export const stepToTargetDraft = (step: StepDto): TargetDraft => {
  const narrowed = narrowStepTarget(step)
  switch (narrowed.targetType) {
    case 'QUEST':
      return {
        kind: 'QUEST',
        quest: narrowed.target ? questDtoToDraft(narrowed.target) : null,
      }
    case 'QUEST_GROUP': {
      const group = narrowed.target
      if (!group) return { kind: 'QUEST_GROUP', group: emptyGroupDraft() }
      return {
        kind: 'QUEST_GROUP',
        group: {
          id: group.id,
          meta: {
            availabilityType: group.availabilityType,
            completeCount: group.completeCount,
            active: group.active,
            ordinal: group.ordinal,
            translations: group.translations ?? [],
          },
          quests: [...(group.quests ?? [])]
            .sort((a, b) => a.ordinal - b.ordinal)
            .map(questDtoToDraft),
          removedQuestIds: [],
          tags: group.tags ?? [],
        },
      }
    }
    case 'ROADMAP':
      return {
        kind: 'ROADMAP',
        roadmapId: narrowed.target?.id ?? step.targetId ?? null,
      }
    default:
      return { kind: 'NONE' }
  }
}

/**
 * = ContinuousQuestSummaryDto on the wire. One active continuous quest somewhere in the festival
 * edition, as returned by `getContinuousQuests`.
 */
export interface ContinuousQuestSummary {
  id: string
  typeKey: QuestTypeKey
  /** Null = open-ended on that side. Both null = live for the whole edition. */
  timeWindowStart?: string | null
  timeWindowEnd?: string | null
  roadmapId?: string | null
  roadmapTitle?: string | null
  title?: string | null
}

/**
 * Do two `[start, end]` windows share at least one instant?
 *
 * <p>A null bound is open-ended on that side, so a window with neither bound overlaps everything.
 * Bounds are INCLUSIVE — mirroring the engine, which admits a signal unless it is strictly before
 * the start or strictly after the end. Two windows that merely touch therefore do overlap: a signal
 * landing on that instant would be evaluated by both quests. Keep this in lockstep with
 * `TimeWindows.overlaps` in the operations module.
 */
export const windowsOverlap = (
  aStart?: string | null,
  aEnd?: string | null,
  bStart?: string | null,
  bEnd?: string | null,
): boolean => {
  const t = (iso?: string | null) => (iso ? new Date(iso).getTime() : null)
  const [as, ae, bs, be] = [t(aStart), t(aEnd), t(bStart), t(bEnd)]
  // Disjoint only if one window ends strictly before the other starts; an open bound on either side
  // of a comparison makes that side unbounded, so it can never be the one that separates.
  const aEndsFirst = ae !== null && bs !== null && ae < bs
  const bEndsFirst = be !== null && as !== null && be < as
  return !aEndsFirst && !bEndsFirst
}

/** True when a window has neither bound — live for the whole edition, so exclusive for its type. */
export const isAlwaysOpen = (
  start?: string | null,
  end?: string | null,
): boolean => !start && !end

/**
 * The first already-configured continuous quest of `typeKey` in the edition whose time window
 * overlaps `[start, end]`, or null when the placement is free.
 *
 * <p>Mirrors `ContinuousQuestPlacementValidator` in operations: one active quest of each continuous
 * type may be open at any instant per FESTIVAL EDITION — not per roadmap. Several of the same type
 * are fine with non-overlapping windows (a different rate per festival day); one with no dates at
 * all is live throughout and therefore the only one the edition can hold. The server remains the
 * source of truth — this exists so the admin finds out before submitting, not after.
 */
export const continuousConflict = (
  existing: ContinuousQuestSummary[],
  typeKey: QuestTypeKey,
  start?: string | null,
  end?: string | null,
  excludeQuestId?: string | null,
): ContinuousQuestSummary | null => {
  if (!CONTINUOUS_QUEST_TYPES.includes(typeKey)) return null
  return (
    existing.find(
      (q) =>
        q.typeKey === typeKey &&
        q.id !== excludeQuestId &&
        windowsOverlap(start, end, q.timeWindowStart, q.timeWindowEnd),
    ) ?? null
  )
}

/** Human-readable window for a conflict message, e.g. `1 Aug 2026 → open`. */
export const describeWindow = (
  start?: string | null,
  end?: string | null,
): string => {
  const fmt = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })
      : 'open'
  return `${fmt(start)} → ${fmt(end)}`
}

/** Returns a validation message for an incomplete target draft, or null if valid. */
export const targetDraftError = (draft: TargetDraft): string | null => {
  switch (draft.kind) {
    case 'NONE':
      return null
    case 'QUEST':
      return draft.quest ? null : 'Configure the quest'
    case 'ROADMAP':
      return draft.roadmapId ? null : 'Select a roadmap'
    case 'QUEST_GROUP': {
      const count = draft.group.quests.length
      if (count < 1) return 'Add at least one quest'
      const complete = draft.group.meta.completeCount ?? 1
      if (complete < 1 || complete > count) {
        return `Complete count must be between 1 and ${count}`
      }
      return null
    }
  }
}

// ---------------------------------------------------------------------------
// Ranks (points thresholds per roadmap; ordered by pointsThreshold ascending)
// ---------------------------------------------------------------------------

/** = RankTranslationDto on the wire. */
export interface RankTranslation {
  id?: string
  locale: string
  name?: string
}

/** = RankDto on the wire. */
export interface RoadmapRank {
  id: string
  roadmapId: string
  pointsThreshold: number
  imageRef?: string
  translations: RankTranslation[]
}

/** = RankUpsertDto on the wire. */
export interface RankUpsert {
  pointsThreshold: number
  imageRef?: string
  translations: RankTranslation[]
}

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export interface BadgeDto {
  id: string
  partnerId?: string | null
  name: string
  iconRef?: string | null
  translations: BadgeTranslationDto[]
}

export interface BadgeUpsertDto {
  partnerId?: string | null
  name: string
  iconRef?: string | null
  translations: BadgeTranslationDto[]
}

// ---------------------------------------------------------------------------
// Reward config (Jackson-polymorphic on `typeKey`; typeKey derived server-side)
// ---------------------------------------------------------------------------

export interface CoinVoucherRewardConfigDto {
  typeKey: 'COIN_VOUCHER'
  coinVoucherId: string
}
export interface DiscountVoucherRewardConfigDto {
  typeKey: 'DISCOUNT_VOUCHER'
  voucherId: string
}
export interface TicketRewardConfigDto {
  typeKey: 'TICKET'
  ticketId: string
}
export interface HotelRewardConfigDto {
  typeKey: 'HOTEL'
  ticketId: string
}
export interface PhysicalRewardConfigDto {
  typeKey: 'PHYSICAL'
}

export type RewardConfigDto =
  | CoinVoucherRewardConfigDto
  | DiscountVoucherRewardConfigDto
  | TicketRewardConfigDto
  | HotelRewardConfigDto
  | PhysicalRewardConfigDto

export const emptyRewardConfig = (typeKey: RewardTypeKey): RewardConfigDto => {
  switch (typeKey) {
    case 'COIN_VOUCHER':
      return { typeKey, coinVoucherId: '' }
    case 'DISCOUNT_VOUCHER':
      return { typeKey, voucherId: '' }
    case 'TICKET':
      return { typeKey, ticketId: '' }
    case 'HOTEL':
      return { typeKey, ticketId: '' }
    case 'PHYSICAL':
      return { typeKey }
  }
}

// ---------------------------------------------------------------------------
// Reward Definition
// ---------------------------------------------------------------------------

export interface RewardDefinitionTranslationDto {
  id?: string
  locale: string
  title?: string
  description?: string
}

/**
 * A reward's distribution mode is the pair (`pointPrice`, `displayOnly`):
 * - price set                      → CLAIM: listed in the app catalog, buyable with points.
 * - no price, `displayOnly` false  → AUTO: hidden from the catalog, granted by a reward rule.
 * - no price, `displayOnly` true   → DISPLAY: listed in the catalog but never granted — an
 *   end-of-festival grand prize or a sponsor showcase item, handed over out-of-band.
 *
 * A price and `displayOnly` are mutually exclusive; the API rejects the combination.
 */
export interface RewardDefinitionDto {
  id: string
  partnerId?: string | null
  festivalEditionId: string
  typeKey: RewardTypeKey
  config: RewardConfigDto
  configVersion: number
  pointPrice?: number | null
  /** Shown in the app catalog but never granted. Never set together with a `pointPrice`. */
  displayOnly: boolean
  rewardPoolId?: string | null
  photoRef?: string | null
  translations: RewardDefinitionTranslationDto[]
}

export interface RewardDefinitionUpsertDto {
  partnerId?: string | null
  /** `typeKey` is derived server-side from config; do not send it separately. */
  config: RewardConfigDto
  pointPrice?: number | null
  /**
   * Shown in the app catalog but never granted. The API rejects it alongside a `pointPrice`, and
   * refuses to set it on a reward that still has auto-grant rules.
   */
  displayOnly?: boolean
  rewardPoolId?: string | null
  photoRef?: string | null
  translations: RewardDefinitionTranslationDto[]
}

// ---------------------------------------------------------------------------
// Reward Rule
// ---------------------------------------------------------------------------

export interface RewardRuleDto {
  id: string
  rewardDefinitionId: string
  triggerType: TriggerType
  triggerRef?: string | null
  rewardPoolId?: string | null
  active: boolean
}

export interface RewardRuleUpsertDto {
  triggerType: TriggerType
  /** Required and must be a UUID when triggerType = CHALLENGE. */
  triggerRef?: string | null
  rewardPoolId?: string | null
  active?: boolean
}

// ---------------------------------------------------------------------------
// Reward Pool
// ---------------------------------------------------------------------------

export interface RewardPoolDto {
  id: string
  partnerId?: string | null
  roadmapId?: string | null
  totalQty?: number | null
  /** Read-only: units already granted. */
  consumed: number
  depletionPolicy: DepletionPolicy
}

export interface RewardPoolUpsertDto {
  partnerId?: string | null
  roadmapId?: string | null
  /** Required when depletionPolicy = BLOCK. */
  totalQty?: number | null
  depletionPolicy: DepletionPolicy
}

// ---------------------------------------------------------------------------
// Leaderboards
// ---------------------------------------------------------------------------

export type LeaderboardScope = 'FESTIVAL' | 'PARTNER'
export type LeaderboardKind = 'DEFAULT' | 'CUSTOM'
export type LeaderboardPeriod = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR' | 'ALL_TIME'

export const LEADERBOARD_SCOPES: LeaderboardScope[] = ['FESTIVAL', 'PARTNER']
export const LEADERBOARD_KINDS: LeaderboardKind[] = ['DEFAULT', 'CUSTOM']
export const LEADERBOARD_PERIODS: LeaderboardPeriod[] = [
  'DAY',
  'WEEK',
  'MONTH',
  'YEAR',
  'ALL_TIME',
]

export interface LeaderboardTranslationDto {
  id?: string
  locale: string
  name?: string
}

export interface LeaderboardDto {
  id: string
  festivalEditionId: string
  scope: LeaderboardScope
  partnerId?: string | null
  kind: LeaderboardKind
  /** Set for server-provisioned DEFAULT boards; null for CUSTOM boards. */
  periodType?: LeaderboardPeriod | null
  /** ISO instant. Set for CUSTOM boards (their explicit window). */
  startsAt?: string | null
  /** ISO instant. Set for CUSTOM boards (their explicit window). */
  endsAt?: string | null
  active: boolean
  /** Display order within the edition, set by an admin. Gaps and duplicates are allowed. */
  ordinal: number
  translations: LeaderboardTranslationDto[]
}

/** Body for creating/updating a CUSTOM leaderboard (DEFAULT boards are server-provisioned). */
export interface LeaderboardUpsertDto {
  scope: LeaderboardScope
  /** Required when scope = PARTNER. */
  partnerId?: string | null
  /** ISO instant; must be before endsAt. */
  startsAt: string
  /** ISO instant; must be after startsAt. */
  endsAt: string
  /** Display order. Omit to keep the current value (0 on create). */
  ordinal?: number
  translations: LeaderboardTranslationDto[]
}

export interface LeaderboardStandingsBoardDto {
  id: string
  /** Resolved (already-localized) display name. */
  name: string
  scope: LeaderboardScope
  partnerId?: string | null
  kind: LeaderboardKind
  periodType?: LeaderboardPeriod | null
  startsAt?: string | null
  endsAt?: string | null
}

/** Resolved (already-localized) roadmap rank tier attained by a standings entry's user. */
export interface LeaderboardEntryRank {
  id: string
  /** Resolved (already-localized) rank name. */
  name?: string
  imageRef?: string
  pointsThreshold: number
  /** Always true for an entry's attained tier. */
  achieved: boolean
}

export interface LeaderboardStandingEntryDto {
  userId: string
  displayName?: string | null
  score: number
  /** 0-based position. */
  rank: number
  /** Null when the user is below the board roadmap's lowest threshold, or the board's roadmap has no ranks. */
  roadmapRank?: LeaderboardEntryRank | null
}

export interface LeaderboardStandingsDto {
  board: LeaderboardStandingsBoardDto
  entries: LeaderboardStandingEntryDto[]
}
